import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  InputAdornment,
  Pagination,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { alpha, useTheme } from '@mui/material/styles';
import AdminDashboard from 'layouts/AdminDashboard';
import Container from 'components/Container';
import { getAccounts, AdminAccountDTO } from './AccountService';

const PAGE_SIZE = 20;

const fmtDate = (ts: string | null) =>
  ts
    ? new Date(ts).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
    : '—';

const SUBSCRIPTION_CHIP: Record<string, { bg: string; color: string; label: string }> = {
  free:             { bg: '#9E9E9E22', color: '#616161', label: 'Free' },
  trialing:         { bg: '#1E88E522', color: '#1565C0', label: 'Trialing' },
  premium:          { bg: '#2E7D3222', color: '#1B5E20', label: 'Premium' },
  expired:          { bg: '#C6282822', color: '#B71C1C', label: 'Expired' },
  cancelled:        { bg: '#C6282822', color: '#B71C1C', label: 'Cancelled' },
  needs_attention:  { bg: '#F9A82522', color: '#E65100', label: 'Needs Attention' },
};

const SubscriptionChip = ({ state }: { state: string | null }) => {
  const cfg = SUBSCRIPTION_CHIP[state ?? 'free'] ?? SUBSCRIPTION_CHIP['free'];
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{
        fontSize: '0.68rem',
        height: 22,
        bgcolor: cfg.bg,
        color: cfg.color,
        fontWeight: 600,
      }}
    />
  );
};

const AccountsPage = (): JSX.Element => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [rows, setRows] = useState<AdminAccountDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    getAccounts(page - 1, PAGE_SIZE)
      .then((res) => {
        setRows(res.data.content);
        setTotal(res.data.totalElements);
      })
      .catch(() => setError('Could not load accounts.'))
      .finally(() => setLoading(false));
  }, [page]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.email.toLowerCase().includes(q) ||
        `${r.firstName} ${r.lastName}`.toLowerCase().includes(q),
    );
  }, [rows, search]);

  const pageCount = Math.ceil(total / PAGE_SIZE);

  return (
    <AdminDashboard>
      <Box
        sx={{
          background: `linear-gradient(to bottom, ${alpha(theme.palette.primary.main, 0.06)}, transparent)`,
          minHeight: '100vh',
          py: 4,
        }}
      >
        <Container>
          {/* Header */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" fontWeight={700}>
              Accounts
            </Typography>
            {!loading && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {total} total accounts
              </Typography>
            )}
          </Box>

          {error && (
            <Card
              sx={{ mb: 3, borderRadius: 3, border: `1px solid ${theme.palette.error.light}` }}
            >
              <CardContent>
                <Typography color="error">{error}</Typography>
              </CardContent>
            </Card>
          )}

          <Card sx={{ borderRadius: 3, boxShadow: theme.shadows[2] }}>
            <CardContent sx={{ p: 3 }}>
              <TextField
                placeholder="Search by name or email…"
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ mb: 3, width: { xs: '100%', sm: 360 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Name / Email</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Subscription</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Household</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Businesses</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Enabled</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Joined</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      Array.from({ length: 6 }).map((_, i) => (
                        <TableRow key={i}>
                          {Array.from({ length: 7 }).map((__, j) => (
                            <TableCell key={j}>
                              <Skeleton width={j === 0 ? 160 : 80} />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : filtered.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                          No accounts found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filtered.map((a) => (
                        <TableRow key={a.id} hover sx={{ cursor: 'default' }}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {a.firstName} {a.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {a.email}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <SubscriptionChip state={a.subscriptionState} />
                          </TableCell>
                          <TableCell>
                            {a.hasHousehold ? (
                              <Box>
                                <Typography variant="body2">{a.householdName ?? '—'}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {a.householdRole} · {a.householdMemberCount} members
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                None
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {a.activeBusinessCount > 0
                                ? `${a.activeBusinessCount} active`
                                : a.draftBusinessCount > 0
                                  ? `${a.draftBusinessCount} draft`
                                  : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={a.enabled ? 'Active' : 'Inactive'}
                              size="small"
                              sx={{
                                fontSize: '0.7rem',
                                height: 22,
                                bgcolor: a.enabled
                                  ? alpha(theme.palette.success.main, 0.12)
                                  : alpha(theme.palette.error.main, 0.1),
                                color: a.enabled
                                  ? theme.palette.success.dark
                                  : theme.palette.error.dark,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {fmtDate(a.createdAt)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => navigate(`/admin/accounts/${a.id}`)}
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {pageCount > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={pageCount}
                    page={page}
                    onChange={(_, v) => setPage(v)}
                    color="primary"
                    size="small"
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Container>
      </Box>
    </AdminDashboard>
  );
};

export default AccountsPage;
